import { Box } from '@material-ui/core';

import { SText } from '@setvi/shared/components';
import { ResourceType } from '@setvi/shared/enums';

import { AnswerSource, DocumentIndexStatus } from 'Services';
import { AILoader } from 'pages/products/components';
import { aiPdfProcessingMessages } from 'pages/products/components/ai/loader/messages';

import { Container, Content } from './styled';
import { Chat } from './tabs';
import { useAskPdf } from './hook';

export interface AskPdfProps {
  /**
   * The numeric `Resource.ID`, stringified — NOT `Resource.ResourceID`.
   * The assistant indexes documents under `Resource.ID`: it is the key of the
   * indexed document, of the ownership check, of the blob path
   * `{companyId}/Resources/{ID}/`, and of the vector namespace. `ResourceID` is
   * a separate opaque string (the delete and favourite calls use that one) and
   * passing it here reaches no document.
   */
  id: string;
  url: string;
  name: string;
  typeId: ResourceType;
  thumbnail: string;
  /**
   * Jump the surrounding PDF viewer to a cited page. When omitted we fall
   * back to opening the file at that page in a new tab — wire this up to
   * the in-app viewer to keep the user in context.
   */
  onCitationClick?: (pageNumber: number) => void;
}

export interface ConversationProps {
  question: string;
  answer: string;
  sources: AnswerSource[];
}

/**
 * Only the question view is shown. The Overview and FAQ tab components are
 * still in ./tabs and their data endpoint still exists server-side, so they
 * can be brought back without rebuilding either.
 */
export enum Tabs {
  AskT = 'Ask Questions'
}

const AskPdf = ({
  id,
  url,
  name,
  typeId,
  thumbnail,
  onCitationClick
}: AskPdfProps) => {
  const {
    error,
    status,
    loading,
    questions,
    initLoading,
    conversation,

    onSubmit
  } = useAskPdf({ id });

  const handleCitationClick = (pageNumber: number) => {
    if (onCitationClick) {
      onCitationClick(pageNumber);
      return;
    }

    if (url) window.open(`${url}#page=${pageNumber}`, '_blank', 'noopener');
  };

  if (initLoading || status === DocumentIndexStatus.Pending)
    return (
      <Container>
        <AILoader messages={aiPdfProcessingMessages} />
      </Container>
    );

  if (
    status === DocumentIndexStatus.Unsupported ||
    status === DocumentIndexStatus.Failed
  )
    return (
      <Container>
        <Box p={4} textAlign="center">
          <SText
            size="lg"
            weight="medium"
            title={
              status === DocumentIndexStatus.Unsupported
                ? 'This PDF has no readable text'
                : 'We could not process this PDF'
            }
          />
          <Box mt={1}>
            <SText
              size="sm"
              fontColor="#6B7280"
              title={
                status === DocumentIndexStatus.Unsupported
                  ? 'It looks like a scanned image. Try a version with selectable text.'
                  : 'Our team has been notified. Please try again later.'
              }
            />
          </Box>
        </Box>
      </Container>
    );

  return (
    <Container>
      <Box
        style={{
          position: 'relative',
          height: '100%'
        }}>
        <Content>
          <Chat
            id={id}
            name={name}
            error={error}
            loading={loading}
            typeId={typeId}
            thumbnail={thumbnail}
            questions={questions}
            conversation={conversation}
            onSubmit={onSubmit}
            onCitationClick={handleCitationClick}
          />
        </Content>
      </Box>
    </Container>
  );
};

export default AskPdf;
